import {
  Box,
  Card,
  CardContent,
  Divider as MuiDivider,
  Grid,
  TextField,
  Typography,
  Tabs,
  Tab,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { spacing } from "@mui/system";
import { FormAccordoProvider, useFormAccordo } from "./ContextAccordo";
import styled from "@emotion/styled";
import Garanzie from "./Garanzie";
import Agenzie from "./Agenzie";
import Condizioni from "./Condizioni";
import { erroreFascia } from "./lib/validaCondizioni";
import { erroreLogo, logoCambiato } from "./lib/logo";
import AutocompleteSelect from "./lib/AutocompleteSelect";
import { Save, ChevronLeft } from "@mui/icons-material";
import Impostazioni from "./Impostazioni";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Documenti from "./Documenti";
import Workflow from "./Workflow";
import { API_BASE_URL } from "../../../config";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/slices/snackbar";

const Divider = styled(MuiDivider)(spacing);

const MAX_NOME = 50;
const MAX_NOTE = 400;

// Tab disponibili: quali mostrare lo decide accordiGetDetails (campo "tabs")
const TABS = [
  { key: "condizioni", label: "Condizioni", component: <Condizioni /> },
  { key: "agenzie", label: "Agenzie", component: <Agenzie /> },
  { key: "garanzie", label: "Garanzie Accessori", component: <Garanzie /> },
  { key: "impostazioni", label: "Impostazioni", component: <Impostazioni /> },
  { key: "documenti", label: "Documenti", component: <Documenti /> },
  { key: "regole", label: "Regole", component: null },
  { key: "workflow", label: "Workflow", component: <Workflow /> },
];

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

function AccordiDetailsComponent() {
  const { formState, dispatch } = useFormAccordo();
  const reduxDispatch = useDispatch();
  const [valueTabs, setValueTabs] = useState(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [errors, setErrors] = useState({});
  const { idUID } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const isNew = !idUID;

  // Accordo nuovo: nessun tab. Dopo il salvataggio i tab arrivano da accordiGetDetails
  const visibleTabs = isNew
    ? []
    : TABS.filter(
        (tab) => !formState.tabs?.length || formState.tabs.includes(tab.key),
      );
  const activeTab = visibleTabs.some((tab) => tab.key === valueTabs)
    ? valueTabs
    : visibleTabs[0]?.key;

  const handleSnackbar = (message, severity) => {
    reduxDispatch(showSnackbar({ message, severity }));
  };

  // Accordo nuovo: crea e poi ricarica la pagina con accordiGetDetails
  const accordiCreate = async (nome, note) => {
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}/api/accordiCreate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        data: {
          nome,
          note,
          idTipologia: formState.accordo.idTipologia,
          idCompagnia: formState.accordo.idCompagnia,
          idProvenienza: formState.accordo.idProvenienza,
        },
      }),
    });
    return response.json();
  };

  const accordiUpdate = async (nome, note) => {
    const {
      tabs,
      dettaglioDocumento,
      options,
      optionDefaults,
      condizioniErrori,
      logoSalvato,
      ...dati
    } = formState;
    // Il logo si invia solo se l'immagine e' cambiata dall'ultimo salvataggio
    const { logo, ...impostazioni } = dati.impostazioni || {};
    const payload = {
      ...dati,
      accordo: { ...dati.accordo, nome, note },
      impostazioni: logoCambiato(logo, logoSalvato)
        ? { ...impostazioni, logo: { nome: logo.nome.trim(), file: logo.file } }
        : impostazioni,
      // idLocale serve solo come chiave delle righe dei documenti nuovi
      documenti: (dati.documenti || []).map(
        ({ idLocale, ...documento }) => documento,
      ),
      // idLocale serve solo come chiave delle righe delle garanzie nuove
      garanzie: (dati.garanzie || []).map(
        ({ idLocale, ...garanzia }) => garanzia,
      ),
    };
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}/api/accordiUpdate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        data: {
          idUID,
          ...payload,
        },
      }),
    });
    return response.json();
  };

  const handleSave = async () => {
    const nome = (formState.accordo.nome || "").trim();
    const note = formState.accordo.note || "";
    const newErrors = {
      nome: nome === "" || nome.length > MAX_NOME,
      note: note.length > MAX_NOTE,
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) {
      handleSnackbar("Controlla i campi evidenziati.", "error");
      return;
    }

    // Ogni condizione deve avere almeno un'operazione
    const condizioni = Array.isArray(formState.condizioni)
      ? formState.condizioni
      : Array.isArray(formState.condizioni?.dataset)
      ? formState.condizioni.dataset
      : [];

    // Accordo esistente senza condizioni: non richiamare accordiUpdate
    const hasTabCondizioni = visibleTabs.some(
      (tab) => tab.key === "condizioni",
    );
    if (hasTabCondizioni && condizioni.length === 0) {
      dispatch({ type: "set_condizioni_errori", payload: true });
      setValueTabs("condizioni");
      handleSnackbar("Aggiungi almeno una fascia nlle condizioni.", "error");
      return;
    }

    const condizioneVuota = condizioni.findIndex(
      (condizione) => !condizione.operazioni?.length,
    );
    if (condizioneVuota !== -1) {
      handleSnackbar(
        `La fascia ${
          condizioneVuota === 0 ? "base" : condizioneVuota
        } deve avere almeno un'operazione.`,
        "error",
      );
      return;
    }

    // Provenienza e condizioni testuali non possono essere vuote
    const erroreCondizioni = condizioni
      .map((fascia) => erroreFascia(fascia))
      .find(Boolean);
    if (erroreCondizioni) {
      dispatch({ type: "set_condizioni_errori", payload: true });
      setValueTabs("condizioni");
      handleSnackbar(erroreCondizioni, "error");
      return;
    }
    dispatch({ type: "set_condizioni_errori", payload: false });

    const errLogo = erroreLogo(
      formState.impostazioni?.logo,
      formState.logoSalvato,
    );
    if (errLogo) {
      setValueTabs("impostazioni");
      handleSnackbar(errLogo, "error");
      return;
    }

    setLoadingPage(true);
    try {
      const responseData = isNew
        ? await accordiCreate(nome, note.trim())
        : await accordiUpdate(nome, note.trim());

      if (responseData.status === -1) {
        navigate("/auth/sign-in");
        return;
      }
      if (responseData.status !== 1) {
        handleSnackbar(
          responseData.message || "Errore durante il salvataggio.",
          "error",
        );
        setLoadingPage(false);
        return;
      }

      handleSnackbar(responseData.message || "Accordo salvato.", "success");
      if (isNew) {
        // Il cambio di idUID richiama accordiGetDetails (il loader resta attivo)
        navigate(`/pages/accordiDetails/${responseData.data.idUid}`, {
          replace: true,
          state: location.state,
        });
      } else {
        await accordiGetDetails(idUID);
        setLoadingPage(false);
      }
    } catch (error) {
      console.log(error);
      handleSnackbar("Errore durante il salvataggio.", "error");
      setLoadingPage(false);
    }
  };

  const handleResponseStatus = (responseData) => {
    if (responseData.status === -1) {
      navigate("/auth/sign-in");
      return false;
    }
    if (responseData.status === 0) {
      navigate("/private");
      return false;
    }
    return true;
  };

  async function accordiGetDetails(idUID) {
    const token = sessionStorage.getItem("token");
    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + token,
      },
      body: JSON.stringify({
        data: {
          idUID: idUID === undefined ? "" : idUID,
        },
      }),
    };

    const response = await fetch(
      `${API_BASE_URL}/api/accordiGetDetails`,
      requestOptions,
    );

    const responseData = await response.json();
    if (!handleResponseStatus(responseData)) return;
    if (responseData.data !== null) {
      dispatch({ type: "SET_DATA_API", payload: responseData.data });
    }
  }

  // Dataset di tutte le select: servono sia per l'accordo nuovo che per quello
  // gia' creato. Con applyDefaults (accordo nuovo) si prendono anche i value
  // preselezionati restituiti dall'API
  async function accordiOptions(applyDefaults) {
    const token = sessionStorage.getItem("token");
    const response = await fetch(`${API_BASE_URL}/api/accordiOptions`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + token,
      },
    });

    const responseData = await response.json();
    if (!handleResponseStatus(responseData)) return;
    if (responseData.data !== null) {
      dispatch({
        type: "SET_OPTIONS",
        payload: { options: responseData.data, applyDefaults },
      });
    }
  }

  const loadAccordo = async (idUID) => {
    setLoadingPage(true);
    try {
      await Promise.all([
        accordiOptions(!idUID),
        ...(idUID ? [accordiGetDetails(idUID)] : []),
      ]);
    } catch (error) {
      console.log(error);
    }
    setLoadingPage(false);
  };

  useEffect(() => {
    loadAccordo(idUID);
  }, [idUID]);

  return (
    <React.Fragment>
      {loadingPage ? (
        <Box
          sx={{
            width: "calc(100vw - 40px)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <React.Fragment>
          <Helmet title="Accordi" />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <Button
                onClick={() => {
                  const fromPath = location.state?.fromPath;
                  if (fromPath) {
                    navigate(fromPath, { state: { fromDetails: true } });
                  } else {
                    navigate(-1);
                  }
                }}
              >
                <ChevronLeft />
              </Button>
              <Typography variant="h3" display="inline">
                {formState.accordo.nome || (isNew ? "Nuovo Accordo" : "")}
              </Typography>
            </Box>
            <Tooltip title="Salva">
              <Button variant="contained" onClick={handleSave}>
                <Save />
              </Button>
            </Tooltip>
          </Box>
          <Divider my={4} />
          <Card sx={{ marginBottom: "16px", paddingTop: "8px" }}>
            <CardContent>
              <Grid
                container
                spacing={3}
                wrap="wrap"
                sx={{ maxWidth: { xs: "calc(100vw - 60px)" } }}
              >
                <Grid item sm={3} xs={12}>
                  <TextField
                    size="small"
                    margin="none"
                    label="Nome"
                    required
                    fullWidth
                    value={formState.accordo.nome}
                    id="nome"
                    inputProps={{ maxLength: MAX_NOME }}
                    error={errors.nome}
                    helperText={
                      errors.nome && !(formState.accordo.nome || "").trim()
                        ? "Il nome è obbligatorio"
                        : ""
                    }
                    onChange={(e) => {
                      setErrors((prev) => ({ ...prev, nome: false }));
                      dispatch({
                        type: "set_accordo",
                        payload: {
                          nome: e.target.value,
                        },
                      });
                    }}
                  />
                </Grid>
                <Grid item sm={3} xs={12}>
                  <AutocompleteSelect
                    id="compagnia"
                    label="Compagnia"
                    options={formState.options.compagnia}
                    value={formState.accordo.idCompagnia}
                    onChange={(value) =>
                      dispatch({
                        type: "set_accordo",
                        payload: { idCompagnia: value },
                      })
                    }
                  />
                </Grid>
                {!isNew && (
                  <Grid item sm={3} xs={12}>
                    <AutocompleteSelect
                      id="stato"
                      label="Stato"
                      options={formState.options.stato}
                      value={formState.accordo.stato}
                      onChange={(value) =>
                        dispatch({
                          type: "set_accordo",
                          payload: { stato: value },
                        })
                      }
                    />
                  </Grid>
                )}
                <Grid item sm={3} xs={12}>
                  <AutocompleteSelect
                    id="tipologia"
                    label="Tipologia"
                    options={formState.options.tipologia}
                    value={formState.accordo.idTipologia}
                    disabled={!isNew}
                    onChange={(value) =>
                      dispatch({
                        type: "set_accordo",
                        payload: { idTipologia: value },
                      })
                    }
                  />
                </Grid>
                <Grid item sm={3} xs={12}>
                  <AutocompleteSelect
                    id="provenienza"
                    label="Provenienza"
                    options={formState.options.provenienza}
                    value={formState.accordo.idProvenienza}
                    disabled={!isNew}
                    onChange={(value) =>
                      dispatch({
                        type: "set_accordo",
                        payload: { idProvenienza: value },
                      })
                    }
                  />
                </Grid>
                {!isNew && (
                  <Grid item sm={3} xs={12}>
                    <AutocompleteSelect
                      id="acquistabile"
                      label="Acquistabile"
                      options={formState.options.acquistabile}
                      value={formState.accordo.acquistabile}
                      onChange={(value) =>
                        dispatch({
                          type: "set_accordo",
                          payload: { acquistabile: value },
                        })
                      }
                    />
                  </Grid>
                )}
                {!isNew && (
                  <Grid item sm={3} xs={12}>
                    <AutocompleteSelect
                      id="visibile"
                      label="Visibile"
                      options={formState.options.visibile}
                      value={formState.accordo.visibile}
                      onChange={(value) =>
                        dispatch({
                          type: "set_accordo",
                          payload: { visibile: value },
                        })
                      }
                    />
                  </Grid>
                )}
                {!isNew && (
                  <Grid item sm={3} xs={12}>
                    <AutocompleteSelect
                      id="fornitore"
                      label="Fornitore"
                      options={formState.options.fornitore}
                      value={formState.accordo.idFornitore}
                      onChange={(value) =>
                        dispatch({
                          type: "set_accordo",
                          payload: { idFornitore: value },
                        })
                      }
                    />
                  </Grid>
                )}
                <Grid item sm={12} xs={12}>
                  <TextField
                    size="small"
                    margin="none"
                    label="Note"
                    fullWidth
                    multiline
                    minRows={2}
                    value={formState.accordo.note}
                    id="note"
                    inputProps={{ maxLength: MAX_NOTE }}
                    error={errors.note}
                    helperText={`${
                      (formState.accordo.note || "").length
                    }/${MAX_NOTE}`} //!! helper text a destra per la lunghezza delle note
                    onChange={(e) => {
                      setErrors((prev) => ({ ...prev, note: false }));
                      dispatch({
                        type: "set_accordo",
                        payload: {
                          note: e.target.value,
                        },
                      });
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {visibleTabs.length > 0 && (
            <>
              <Divider my={4} />
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setValueTabs(newValue)}
                sx={{ marginTop: "16px" }}
              >
                {visibleTabs.map((tab) => (
                  <Tab key={tab.key} value={tab.key} label={tab.label} />
                ))}
              </Tabs>
              <Card sx={{ marginTop: "16px" }}>
                <CardContent>
                  {visibleTabs.map((tab) => (
                    <CustomTabPanel
                      key={tab.key}
                      value={activeTab}
                      index={tab.key}
                    >
                      {tab.component}
                    </CustomTabPanel>
                  ))}
                </CardContent>
              </Card>
            </>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

const AccordiDetails = () => (
  <FormAccordoProvider>
    <AccordiDetailsComponent />
  </FormAccordoProvider>
);

export default AccordiDetails;
